import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useThreadMessages } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { api, ApiUnreachableError, ResponseValidationError } from '@/api/client'

/**
 * Shape of a single suggestion object as embedded on a message's
 * `suggestions.latest` (see ThreadOrchestrationMessage below) - only the
 * fields this hook and its consumers (e.g. src/components/messages/
 * composer.tsx) actually read.
 */
export interface ThreadOrchestrationSuggestion {
  id?: string | null
  message_id?: string | null
  version?: number | null
  status?: string
  text?: string
  sent_at?: string | null
}

/**
 * Shape of a single message as returned by useThreadMessages (GET
 * /admin/threads/:threadId/messages) - only the fields this hook actually
 * reads. The API client itself is still untyped (src/api/client.js), so
 * this is the "shape I need" this hook narrows the response to, mirroring
 * the pattern already used by src/components/messages/thread-pane.tsx and
 * src/components/messages/composer.tsx.
 */
export interface ThreadOrchestrationMessage {
  message_id: string
  direction?: string
  suggestions?: {
    latest?: ThreadOrchestrationSuggestion | null
  } | null
  body?: string
  sent_at?: string
  channel?: string
  /**
   * Present only on a client-synthesized message inserted by an in-flight or
   * recently-completed Send (see pendingSends/handleSend below) - absent on
   * every real message the backend returns. Never written into the
   * TanStack Query cache; consumers (thread-pane.tsx) key their status
   * glyph/Resend UI off this field.
   */
  pendingStatus?: PendingSendStatus
  pendingError?: string
  pendingIdempotencyKey?: string
}

/**
 * iMessage-style lifecycle of one optimistically-rendered sent message,
 * keyed by its idempotencyKey (see PendingSendEntry) - `sending` on submit,
 * `sent` once admin-api's send endpoint accepts it (Uplisting's own
 * message_created webhook, and this thread's canonical message list, may
 * still be catching up), `failed` on a definitive rejection, `ambiguous` on
 * admin-api's `uplisting_send_ambiguous` 502 (Uplisting's own delivery
 * outcome is unknown - resending carries real duplicate-message risk,
 * unlike `failed` where the send definitely never reached Uplisting).
 */
export type PendingSendStatus = 'sending' | 'sent' | 'failed' | 'ambiguous'

/**
 * One in-flight or recently-completed Send, tracked in hook-local state
 * (never the TanStack Query cache - see handleSend) so it can render
 * immediately and be reconciled/dropped once the canonical message arrives.
 * Carries its own messageId/version/text (captured at send time) rather
 * than reading them off `liveMessage` at resend time, because `liveMessage`
 * itself stops resolving to this same guest message the instant this entry
 * exists (see the `messages` memo below) - there is nothing to re-derive
 * these from once the pending entry has been inserted.
 */
export interface PendingSendEntry {
  /** This specific network attempt's own key - the pendingSends Map key,
   * and what actually goes out on the wire for THIS attempt. A resend of
   * an `ambiguous` entry gets a brand-new one here (see
   * handleResendPendingMessage - the original's idempotency claim is
   * deliberately retained by admin-api, so reusing it would just hit that
   * claim instead of performing a real retry). */
  idempotencyKey: string
  /** The FIRST attempt's key in this send's whole retry chain, stable
   * across every resend regardless of how many attempts and keys came
   * after it (Codex PR review) - this, not `idempotencyKey` above, is what
   * Composer.tsx's stale-draft-clearing effect matches against, since
   * Composer only ever held the original key and has no way to know about
   * a resend chain's later, internal keys. */
  originalIdempotencyKey: string
  /** Thread this send was started for - see handleSend's startedForThreadId
   * comment for why every async continuation here pins its own thread ID
   * rather than re-reading the (possibly since-navigated-away) hook prop. */
  threadId: string | undefined
  messageId: string
  version: number
  text: string
  /** The guest message's own channel (liveMessage.channel at send time) -
   * a real fact, not a guess: a reply always goes out on the same
   * channel/thread as the guest message it answers. Used only so
   * thread-pane.tsx's channel badge doesn't fall back to a misleading
   * default while this entry is still pending. */
  channel?: string
  status: PendingSendStatus
  error?: string
  /** Set once the send succeeds - the correlation effect below matches this
   * against a canonical message's own `"uplisting_v3:" + uplistingMessageId`
   * id to know when the real record has arrived and this entry can be
   * dropped. */
  uplistingMessageId?: string | null
  /** True once ANY attempt in this retry chain has come back ambiguous and
   * has not since been confirmed `sent` (Codex PR review). A LATER
   * attempt's own definitive rejection only proves that specific retry
   * didn't go through - it says nothing about whether an earlier,
   * genuinely ambiguous attempt already delivered the message. Once set,
   * `status` is never allowed to fall back to a plain `failed`, which
   * would falsely claim the message is known to have never sent and
   * silently green-light a same-key, no-duplicate-risk plain Resend. */
  hasUnresolvedAmbiguity: boolean
}

/**
 * Shape of the useThreadMessages response this hook reads from.
 */
export interface ThreadOrchestrationData {
  messages?: ThreadOrchestrationMessage[]
  property_id?: string | null
  reservation_id?: string | null
  needs_reply?: boolean
}

export interface UseThreadOrchestrationParams {
  /** Thread to drive, or undefined when no thread is selected yet. */
  threadId: string | undefined
  /**
   * When given, the fetched thread's own `property_id` is validated against
   * this - a stale or malformed URL combining one property with a thread
   * that actually belongs to a different one must never drive that
   * property's mark-read/send/regenerate effects (Codex PR review, ported
   * from property-messages.jsx). Computed internally (not accepted as a
   * plain boolean from the caller) so callers don't need `threadData` -
   * which this same hook call is what produces - just to decide what to
   * pass in. Omit for a caller with no such scope to validate against (e.g.
   * the global Inbox, where any thread is valid).
   */
  expectedPropertyId?: string
  /**
   * React Query key prefix for the conversation-list cache to invalidate
   * after a mark-read success, a sent reply, or a needs-reply dismissal
   * (e.g. `['property-threads-page', propertyId]` or `['threads-page']`) -
   * a prefix invalidates every cached page/filter variant under it.
   */
  listQueryKeyPrefix: readonly unknown[]
}

export interface ThreadOrchestrationSendMeta {
  idempotencyKey: string
  acceptedSuggestion?: ThreadOrchestrationSuggestion | null
}

/**
 * Same guest-direction aliases recognized by message-bubble.jsx and
 * thread-pane.jsx's own isGuestMessage() - a payload using the legacy
 * 'inbound'/'guest' values (or omitting direction) must still count as a
 * guest message here too, not just the normalized 'guest_to_host' value.
 */
function isGuestDirection(direction: string | undefined): boolean {
  return (
    direction === 'guest_to_host' || direction === 'guest' || direction === 'inbound' || !direction
  )
}

// Only the thread's last message can carry a live, sendable suggestion
// (mirrors admin-api's buildThreadMessagesPayload rule) - if the host
// already replied last, there is nothing pending to attach a send to.
function getLiveMessage(
  messages: ThreadOrchestrationMessage[] | null | undefined
): ThreadOrchestrationMessage | null {
  const list = messages ?? []
  const lastMessage = list.length > 0 ? list[list.length - 1] : null
  return lastMessage && isGuestDirection(lastMessage.direction) ? lastMessage : null
}

function isSuggestionInProgress(status: string | undefined | null): boolean {
  const normalized = (status || '').toLowerCase()
  return normalized === 'queued' || normalized === 'generating' || normalized === 'streaming'
}

// How often useThreadMessages re-polls while a suggestion is in progress -
// see the refetchInterval callback below.
const SUGGESTION_POLL_INTERVAL_MS = 2000
// A stuck backend job (queued/generating/streaming forever) must not poll
// this thread indefinitely for as long as the inbox stays open (Codex PR
// review) - bounded the same way suggested-review-block.jsx bounds its own
// polling, and for the same reason: past this many fetches without a
// terminal status, automatic polling stops and the "Check again" control
// below takes over.
const MAX_SUGGESTION_POLL_ATTEMPTS = 15

// A failed mark-read POST for an ordinary (non-polling) thread has no future
// fetch to piggyback a retry off of (Codex PR review) - only a thread with a
// suggestion still in progress polls on its own. Without an explicit retry,
// a single transient failure would leave that thread unread for the rest of
// the visit. Bounded the same way the suggestion poll above is bounded.
const MARK_READ_RETRY_DELAY_MS = 3000
const MAX_MARK_READ_RETRY_ATTEMPTS = 3

// How much of the thread pane's scroll container must actually be on
// screen before it counts as "visible" for mark-read purposes (a further
// Codex PR review): IntersectionObserver's own `isIntersecting` boolean is
// simply intersectionRatio > 0, NOT gated by `threshold` - a single pixel
// of overlap on the stacked (below-`lg`) layout would already read as
// visible while the actual messages remain effectively off-screen.
const PANE_VISIBILITY_RATIO = 0.25

// Counts fetches that resolved to an error too, not just successful ones -
// dataUpdateCount alone never advances while the endpoint is failing, so a
// budget based on it alone would never run out (mirrors the identical
// helper in suggested-review-block.jsx).
function getFetchAttemptCount(
  queryState: { dataUpdateCount?: number; errorUpdateCount?: number } | undefined
): number {
  return (queryState?.dataUpdateCount ?? 0) + (queryState?.errorUpdateCount ?? 0)
}

/**
 * Clear `needs_reply` on a cached `thread-messages` payload (CAP-355).
 *
 * Deliberately conservative: anything that is not a plain object carrying
 * the field is returned untouched, so an unexpected cache shape degrades to
 * "no optimistic update" rather than to a corrupted cache entry.
 */
function withThreadNeedsReplyCleared(current: unknown): unknown {
  if (!current || typeof current !== 'object') return current
  if (!('needs_reply' in (current as Record<string, unknown>))) return current
  return { ...(current as Record<string, unknown>), needs_reply: false }
}

/**
 * Clear `needsReply` on one thread's row in a cached conversation list
 * (CAP-355). The list is an infinite query, so the rows live under
 * `pages[].items[]`; every page is walked because the thread can sit on
 * any of them, and the object identity of untouched pages is preserved so
 * React Query's consumers only re-render for the page that changed.
 */
function withListNeedsReplyCleared(current: unknown, threadId: string): unknown {
  if (!current || typeof current !== 'object') return current
  const pages = (current as { pages?: unknown }).pages
  if (!Array.isArray(pages)) return current

  let changed = false
  const nextPages = pages.map(page => {
    if (!page || typeof page !== 'object') return page
    const items = (page as { items?: unknown }).items
    if (!Array.isArray(items)) return page

    let pageChanged = false
    const nextItems = items.map(item => {
      if (!item || typeof item !== 'object') return item
      const row = item as { threadId?: unknown; needsReply?: unknown }
      if (row.threadId !== threadId || row.needsReply !== true) return item
      pageChanged = true
      return { ...row, needsReply: false }
    })
    if (!pageChanged) return page
    changed = true
    return { ...(page as Record<string, unknown>), items: nextItems }
  })

  if (!changed) return current
  return { ...(current as Record<string, unknown>), pages: nextPages }
}

/**
 * Everything a three-pane messages inbox needs to drive a single open
 * thread, independent of whether the inbox is scoped to one property
 * (property-messages.jsx) or global (inbox.jsx): the live-suggestion poll
 * (with a bounded budget and a "Check again" fallback once exhausted),
 * on-screen-visibility-gated mark-read (IntersectionObserver + document
 * visibility, with a bounded retry so a transient failure can't leave a
 * thread unread for the rest of the visit), auto-scroll-to-latest, and the
 * send/regenerate/dismiss-needs-reply handlers. Extracted out of
 * property-messages.jsx (Codex-hardened over many review rounds - see this
 * file's own comments for the specific bugs each piece guards against) so
 * a second page consuming the same three panes doesn't duplicate this
 * correctness-critical logic.
 */
export function useThreadOrchestration({
  threadId,
  expectedPropertyId,
  listQueryKeyPrefix,
}: UseThreadOrchestrationParams) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Per-thread poll budget baseline (Map<threadId, fetchAttemptCountAtStart>)
  // for the refetchInterval below - a ref rather than state since it's read
  // and written from react-query's own callback, not from render. Keyed by
  // thread (not a single shared value) so exhausting one thread's budget,
  // or resetting it via Generate/Check-again, can never affect another's -
  // mirrors pollBaselineRef in suggested-review-block.jsx.
  const pollBaselineRef = useRef(new Map<string | undefined, number>())

  // Every in-flight or recently-completed Send across every thread this hook
  // instance has driven this session, keyed by idempotencyKey - see
  // PendingSendEntry and handleSend. Deliberately hook-local React state,
  // never written into the TanStack Query cache (a real refetch must never
  // meet a client-fabricated record in server state) - and deliberately not
  // reset on a threadId change, so switching threads and back still shows a
  // still-in-flight or just-completed send instead of losing it, which is
  // the exact bug this ticket exists to fix (a refetch racing the operator's
  // own navigation, not just Uplisting's webhook).
  const [pendingSends, setPendingSends] = useState(new Map<string, PendingSendEntry>())
  // The idempotencyKey of the most recent Send that was successfully
  // retried via thread-pane.tsx's inline Resend/Resend-anyway action (NOT
  // through Composer's own Send button) - see handleResendPendingMessage.
  // Composer.tsx watches this to clear a stale preserved draft that this
  // action just delivered out from under it.
  const [lastInlineResendSentKey, setLastInlineResendSentKey] = useState<string | null>(null)

  const threadQuery = useThreadMessages(threadId, {
    // Keeps polling on its own whenever EITHER the thread's live suggestion
    // is still in progress OR this thread has a Send that succeeded but
    // hasn't yet been correlated against the canonical message list (a
    // `sent` pendingSends entry) - whether the suggestion generation was
    // started from this page (Generate/Regenerate below) or was already in
    // flight when the operator opened or reloaded this thread (Codex PR
    // review: useThreadMessages had no refetch interval at all, so an
    // already-queued suggestion showed "Thinking…" forever with nothing
    // ever checking whether the backend had actually finished). Naturally
    // scoped per thread - queryKey includes threadId - so switching
    // threads can never let one thread's in-progress poll affect another.
    // Bounded (Codex PR review): a job stuck queued/generating/streaming
    // forever (or, now, a send whose webhook never lands) must not poll
    // indefinitely for as long as the inbox stays open - past
    // MAX_SUGGESTION_POLL_ATTEMPTS fetches since this thread was first seen
    // in progress, polling stops and isPollExhausted/
    // isSendConfirmationExhausted (below) drive the shared "Check again"
    // control instead.
    refetchInterval: query => {
      const data = query.state.data as ThreadOrchestrationData | undefined
      const liveMsg = getLiveMessage(data?.messages)
      const queryThreadId = query.queryKey[1] as string | undefined
      const hasSentPendingForThisQuery = Array.from(pendingSends.values()).some(
        entry => entry.threadId === queryThreadId && entry.status === 'sent'
      )
      if (
        !isSuggestionInProgress(liveMsg?.suggestions?.latest?.status) &&
        !hasSentPendingForThisQuery
      ) {
        // Neither reason to poll applies right now - clear any baseline so
        // the NEXT time this thread needs polling (a fresh manual
        // generation, or a fresh send) starts its own window instead of
        // resuming a stale one.
        pollBaselineRef.current.delete(queryThreadId)
        return false
      }
      if (!pollBaselineRef.current.has(queryThreadId)) {
        pollBaselineRef.current.set(queryThreadId, getFetchAttemptCount(query.state))
      }
      const attempts =
        getFetchAttemptCount(query.state) - (pollBaselineRef.current.get(queryThreadId) ?? 0)
      return attempts < MAX_SUGGESTION_POLL_ATTEMPTS ? SUGGESTION_POLL_INTERVAL_MS : false
    },
    // Consecutive polls of a still-"queued" suggestion return
    // structurally-identical data, so TanStack Query's default
    // tracked-properties optimization sees no change and skips
    // re-rendering - which would silently break isPollExhausted below:
    // the query's own dataUpdateCount keeps advancing internally
    // (refetchInterval sees it and correctly stops polling), but the
    // component never gets a render to notice and show the "Check again"
    // fallback (identical concern to suggested-review-block.jsx).
    notifyOnChangeProps: 'all',
  })
  const threadData = threadQuery.data as ThreadOrchestrationData | undefined
  // Only a genuine, affirmatively-known mismatch is flagged - a payload that
  // simply lacks property_id (older data) is not treated as one (Codex-
  // caught requirement, ported from property-messages.jsx: a stale/
  // malformed URL combining property A with a thread belonging to property
  // B must never populate property A's inbox with property B's data). No
  // expectedPropertyId (the global Inbox) means this is always false.
  const propertyMismatch =
    !!expectedPropertyId &&
    !!threadId &&
    !!threadData &&
    !!threadData.property_id &&
    threadData.property_id !== expectedPropertyId

  // Tracks whether the thread pane is ACTUALLY visible on screen, via
  // IntersectionObserver on its own scroll container (two further rounds of
  // Codex PR review): neither "viewMode === 'list'" nor "above the lg
  // breakpoint" is a reliable proxy for this - the pane can be unmounted
  // (calendar view), or mounted but scrolled out of view below a long
  // conversation list (any viewport below `lg`, where the grid stacks
  // vertically). A breakpoint-only gate went too far the other way:
  // permanently refusing to mark anything read for the ENTIRE lifetime of
  // a narrow-viewport session, even after the operator scrolls down and
  // genuinely reads the pane - phones and narrow windows would never clear
  // an unread indicator at all. Observing real intersection instead handles
  // every case with one mechanism: not rendered, rendered-but-off-screen,
  // and scrolled-into-view all report the correct answer on their own.
  // Attached via a callback ref (not a plain ref + separate effect)
  // because the underlying DOM node is unmounted/remounted across
  // calendar/list view switches and thread changes - a callback ref
  // reliably re-attaches the observer to whatever node currently exists.
  const threadScrollRef = useRef<HTMLDivElement | null>(null)
  const [isPaneVisible, setIsPaneVisible] = useState(false)
  const threadScrollObserverRef = useRef<IntersectionObserver | null>(null)
  const setThreadScrollNode = useCallback((node: HTMLDivElement | null) => {
    threadScrollRef.current = node
    if (threadScrollObserverRef.current) {
      threadScrollObserverRef.current.disconnect()
      threadScrollObserverRef.current = null
    }
    if (!node) {
      setIsPaneVisible(false)
      return
    }
    const observer = new IntersectionObserver(
      // entries[0].isIntersecting is NOT gated by `threshold` (a further
      // Codex PR review): per spec it's simply intersectionRatio > 0, so a
      // single pixel of overlap on the stacked layout would already read as
      // "visible" while the actual messages remain effectively off-screen.
      // `threshold` only controls how many times the callback fires as the
      // ratio crosses those values - checking intersectionRatio directly
      // against the intended cutoff is what actually enforces it.
      entries => setIsPaneVisible((entries[0]?.intersectionRatio ?? 0) >= PANE_VISIBILITY_RATIO),
      { threshold: PANE_VISIBILITY_RATIO }
    )
    observer.observe(node)
    threadScrollObserverRef.current = observer
  }, [])

  // IntersectionObserver reports geometric intersection only - it keeps
  // reporting a backgrounded/minimized tab's pane as intersecting, since its
  // layout is unaffected by the tab losing visibility (a further Codex PR
  // review): switching away while the opening refetch or a suggestion poll
  // is still in flight would otherwise mark a guest message read the instant
  // it arrives, even though the operator was never looking at the screen.
  // document.visibilityState is the piece IntersectionObserver can't cover -
  // tracked separately here and combined with isPaneVisible below.
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    () => document.visibilityState !== 'hidden'
  )
  useEffect(() => {
    const handleVisibilityChange = () => setIsDocumentVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Captures whatever dataUpdatedAt this thread's query already had - fresh
  // cache included - as the baseline for THIS visit, then forces a fresh
  // refetch (Codex PR review): useThreadMessages' own 30s staleTime means
  // revisiting a recently-viewed thread can hand back a snapshot that
  // predates a guest message which arrived in the meantime, and marking
  // read off that alone (isSuccess is already true for fresh cached data,
  // before any real fetch for this visit has even started) would clear the
  // unread indicator before the message has appeared on screen - a further
  // Codex PR review caught this exact regression in an earlier version of
  // this fix. Runs before the mark-read effect below (both fire in the same
  // commit on a threadId change - effects run in definition order), so its
  // baseline is always set before that effect's own check reads it.
  const openBaselineRef = useRef<{ threadId: string | null; dataUpdatedAt: number }>({
    threadId: null,
    dataUpdatedAt: -1,
  })
  useEffect(() => {
    if (!threadId || propertyMismatch) return
    openBaselineRef.current = { threadId, dataUpdatedAt: threadQuery.dataUpdatedAt }
    threadQuery.refetch().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, propertyMismatch])

  // Marks the thread read once dataUpdatedAt has advanced PAST this visit's
  // baseline - proof a real fetch (the forced refetch above, or a later
  // successful automatic retry if that one failed) has completed since the
  // thread was opened, not merely that cached data already existed. Still
  // fires for any later successful fetch, not just the first one (a Codex
  // PR review on an earlier version: chaining only off the forced refetch's
  // own promise meant a subsequent successful retry never got a chance to
  // mark the thread read). Best-effort: a failure here shouldn't block
  // viewing the thread, so it's silently swallowed rather than surfaced as
  // a toast.
  const lastMarkedReadRef = useRef<{ threadId: string | undefined; signature: string | null }>({
    threadId: undefined,
    signature: null,
  })
  const markReadInFlightRef = useRef(new Set<string | undefined>())
  const pendingRetryRef = useRef(
    new Map<string | undefined, { idsToSend: string[]; sig: string; seqNum: number }>()
  )
  const markReadSeqRef = useRef(new Map<string | undefined, number>())
  // Highest per-thread sequence number that has actually SUCCEEDED (not
  // just been assigned - see markReadSeqRef above) - lets the bounded
  // retry timer recognize when a newer snapshot has already been marked
  // read while it was waiting, even under a DIFFERENT signature (a further
  // Codex PR review): comparing against lastMarkedReadRef's signature
  // alone only catches "this exact snapshot already succeeded", not "a
  // NEWER one already did", so a stale retry could still replay an old
  // snapshot after a newer one succeeded - overwriting lastMarkedReadRef
  // back to the stale signature and making the next identical poll for the
  // newer snapshot look unmarked, repeating the mutation and list reload.
  const lastMarkedSeqRef = useRef(new Map<string | undefined, number>())
  useEffect(() => {
    // Gated on the thread pane's actual on-screen visibility (three rounds
    // of Codex PR review - see isPaneVisible's own comment for why neither
    // viewMode nor a breakpoint check alone was sufficient) AND on the
    // document itself being visible (a further Codex PR review - see
    // isDocumentVisible's own comment): marking read off a snapshot the
    // operator never actually saw defeats the whole point of this effect.
    if (
      !threadId ||
      propertyMismatch ||
      !threadQuery.isSuccess ||
      !isPaneVisible ||
      !isDocumentVisible
    ) {
      return
    }
    // Narrowed once, here, for the rest of this effect run: `threadId` is
    // guaranteed truthy past the guard above and stays that way for the
    // lifetime of this closure (a new render creates a new effect run with
    // its own `threadId`), including inside `attemptMarkRead` and its
    // async `.then`/`.catch`/`.finally`/`setTimeout` continuations below -
    // TS just can't see that through the nested-closure boundary.
    const activeThreadId: string = threadId
    const baseline = openBaselineRef.current
    if (baseline.threadId !== threadId || threadQuery.dataUpdatedAt <= baseline.dataUpdatedAt) {
      return
    }
    // Bounded to the ids actually present in THIS fetched snapshot (two
    // rounds of Codex PR review): an id-based bound can't be fooled by a
    // delayed/retried guest message stamped with an EARLIER occurred_at
    // than the snapshot's own messages, unlike a timestamp cutoff (an
    // earlier version of this sent the last message's sent_at instead).
    // An empty snapshot (a brand-new thread with zero messages) skips the
    // call entirely rather than falling back to the backend's unbounded
    // live-state path - there is nothing here to bound against, and a
    // guest message ingested between this GET and the read POST would
    // otherwise get marked read despite never being displayed.
    const currentMessages =
      (threadQuery.data as ThreadOrchestrationData | undefined)?.messages ?? []
    if (currentMessages.length === 0) return
    // Known residual limitation, deliberately not fixed here (a further Codex PR review):
    // "observed" means "present in the fetched snapshot", not "individually scrolled past
    // on screen". The scroll-to-bottom effect below jumps straight to the newest message, so
    // when a backlog of unread guest messages taller than the pane arrives between visits,
    // the earlier messages in that backlog are marked read here even though the operator
    // never actually scrolled up to see them - only the tail that fits in one viewport after
    // the jump is genuinely seen. A fully correct fix needs real per-message visibility or
    // scroll-progress tracking (e.g. per-message IntersectionObservers, or geometry-based
    // "which messages fall within the container's current scrolled viewport" checks against
    // real layout) - genuine new instrumentation this component doesn't have today, and one
    // jsdom can't simulate without extensive per-test geometry mocking (it has no real
    // layout engine - every element's bounding rect is zeroed). The exposure is bounded to
    // conversations with a backlog of guest messages exceeding one viewport's height between
    // visits, which is narrower than a single reply-and-wait exchange - the common case here.
    const observedMessageIds = currentMessages.map(message => message.message_id)
    // Deduped by the observed snapshot's own identity, not dataUpdatedAt (a
    // further Codex PR review): a suggestion still generating polls this
    // query every ~2s (useThreadMessages) and can return byte-identical
    // messages many times over, advancing dataUpdatedAt on every successful
    // poll even though nothing new was observed - deduping on dataUpdatedAt
    // alone resent the same mark-read call (and re-invalidated the
    // conversation list) on every single poll.
    const signature = `${observedMessageIds[observedMessageIds.length - 1]}:${observedMessageIds.length}`
    const already =
      lastMarkedReadRef.current.threadId === threadId &&
      lastMarkedReadRef.current.signature === signature
    if (already) return

    // Sends the mark-read call for a given snapshot, or - if a call for
    // this SAME thread is already in flight - remembers this as the latest
    // pending snapshot instead of dropping it (a further Codex PR review):
    // clearing markReadInFlightRef in the settled call's `finally` triggers
    // neither a render nor another effect run, so a newer snapshot for the
    // same thread arriving while an older call is still in flight would
    // otherwise be silently lost - especially reproducible when a
    // suggestion poll delivers a new last guest message and then stops
    // polling, leaving that message unread for the rest of the visit. Once
    // the in-flight call settles, any pending snapshot still not already
    // marked read is retried the same way.
    // Monotonic per-thread sequence number, assigned once per distinct
    // snapshot this effect ever decides to send (not per retry attempt) -
    // lets a stale retry recognize it must never clobber a genuinely newer
    // pending snapshot (a further Codex PR review): without this, an older
    // snapshot A's failed attempt could schedule a retry, a newer B could
    // start and go in flight, a still-newer C could queue up behind B in
    // pendingRetryRef - and if A's retry timer then fires while B is still
    // in flight, it would see the thread as busy and overwrite C's pending
    // entry with itself (plain "last write wins"), so B's own `finally`
    // would retry stale A and silently drop C, leaving the newest messages
    // unread with no future poll left to recover them.
    const seq = (markReadSeqRef.current.get(threadId) ?? 0) + 1
    markReadSeqRef.current.set(threadId, seq)

    function attemptMarkRead(idsToSend: string[], sig: string, seqNum: number, attempt = 0): void {
      // Keyed per threadId, not a single shared flag (a further Codex PR
      // review): a shared flag meant switching from thread A (whose read
      // POST was still pending) to thread B blocked B's OWN unrelated
      // effect run entirely.
      if (markReadInFlightRef.current.has(threadId)) {
        const pending = pendingRetryRef.current.get(threadId)
        // Only overwrite an already-queued pending snapshot with one that's
        // at least as new (by sequence number, not arrival order at this
        // check) - a stale retry must never bump a fresher snapshot out of
        // the pending slot.
        if (!pending || seqNum >= pending.seqNum) {
          pendingRetryRef.current.set(threadId, { idsToSend, sig, seqNum })
        }
        return
      }
      markReadInFlightRef.current.add(threadId)
      api
        .markThreadRead(activeThreadId, idsToSend)
        .then(() => {
          // Recorded only on success (a further Codex PR review): a
          // transient mutation failure must not "consume" this signature,
          // or a later identical poll would exit through the `already`
          // check above without ever retrying, leaving the thread unread
          // for the rest of the visit.
          lastMarkedReadRef.current = { threadId, signature: sig }
          lastMarkedSeqRef.current.set(
            threadId,
            Math.max(seqNum, lastMarkedSeqRef.current.get(threadId) ?? -1)
          )
          queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix })
        })
        .catch(() => {
          // Explicit bounded retry for a thread with no future poll to rely
          // on (a further Codex PR review) - see MARK_READ_RETRY_DELAY_MS's
          // own comment for why. Compares seqNum against the highest
          // sequence that has actually SUCCEEDED, not just whether THIS
          // exact signature did (a further Codex PR review): a newer
          // snapshot can succeed under a DIFFERENT signature while this
          // retry is waiting, and comparing signatures alone wouldn't
          // catch that - replaying this stale one would overwrite
          // lastMarkedReadRef back to the older signature, making the next
          // identical poll for the newer snapshot look unmarked and
          // repeat the mutation and list reload.
          if (attempt < MAX_MARK_READ_RETRY_ATTEMPTS) {
            setTimeout(() => {
              const stillNeeded = seqNum > (lastMarkedSeqRef.current.get(threadId) ?? -1)
              if (stillNeeded) attemptMarkRead(idsToSend, sig, seqNum, attempt + 1)
            }, MARK_READ_RETRY_DELAY_MS)
          }
        })
        .finally(() => {
          markReadInFlightRef.current.delete(threadId)
          const pending = pendingRetryRef.current.get(threadId)
          if (!pending) return
          pendingRetryRef.current.delete(threadId)
          // Compares seqNum against the highest sequence that has actually
          // SUCCEEDED, not signature equality alone (a further Codex PR
          // review): a stale snapshot can sit queued in pendingRetryRef
          // while a DIFFERENT, newer snapshot succeeds through some other
          // path (its own retry timer, in this exact case) - signature
          // equality alone would never match a different signature and
          // would dispatch the stale one anyway, overwriting
          // lastMarkedReadRef backward and making the next identical poll
          // for the newer snapshot look unmarked.
          //
          // Also checked directly against this signature (a further Codex
          // PR review): when this call's own POST outlasts a suggestion
          // poll interval, an identical-content poll can queue behind it
          // with a HIGHER seqNum purely because it arrived later, even
          // though it observed nothing new - the seqNum check alone would
          // then dispatch a byte-identical duplicate mutation once this
          // call succeeds, repeating the read POST and list invalidation
          // for no reason.
          const pendingAlreadySuperseded =
            pending.seqNum <= (lastMarkedSeqRef.current.get(threadId) ?? -1) ||
            (lastMarkedReadRef.current.threadId === threadId &&
              lastMarkedReadRef.current.signature === pending.sig)
          if (!pendingAlreadySuperseded) {
            attemptMarkRead(pending.idsToSend, pending.sig, pending.seqNum)
          }
        })
    }

    attemptMarkRead(observedMessageIds, signature, seq)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    threadId,
    propertyMismatch,
    threadQuery.isSuccess,
    threadQuery.dataUpdatedAt,
    isPaneVisible,
    isDocumentVisible,
  ])

  // Drops a `sent` pendingSends entry once the canonical message it
  // predicted has actually arrived, matching admin-api's own id convention
  // (storeUplistingV3Message writes `"uplisting_v3:" + v3Message.id`,
  // surfaced as each message's own `message_id` - see
  // OPTIMISTIC_MESSAGE_SENDING_PLAN.md) - no new backend field needed.
  // Reads the CANONICAL list only (threadData?.messages), never the merged
  // `messages` below, which also contains the pending entries this effect
  // exists to remove.
  //
  // Also re-runs on every `pendingSends` change, not just a new canonical
  // fetch (Codex PR review): the canonical record can already be sitting
  // in `threadData.messages` from an EARLIER fetch - e.g. the operator
  // navigated away and back while the send POST was still in flight -
  // before this same entry had a `uplistingMessageId` to match against at
  // all (`sending`, not yet `sent`). That earlier run correctly skipped it
  // (nothing to correlate yet). Once the POST resolves and performSend
  // marks the entry `sent` with its `uplistingMessageId`, TanStack Query's
  // structural sharing keeps `threadData.messages` at the exact same
  // reference (no new fetch happened) - a dependency on that alone would
  // never re-run this effect again, leaving a permanent duplicate bubble
  // that not even "Check again" can clear. Depending on `pendingSends`
  // too closes that gap; the `changed` guard below still stops the loop
  // once there's nothing left to correlate on the next run.
  useEffect(() => {
    const canonical = threadData?.messages ?? []
    if (canonical.length === 0) return
    setPendingSends(current => {
      let changed = false
      const next = new Map(current)
      for (const [key, entry] of current) {
        if (entry.status !== 'sent' || !entry.uplistingMessageId) continue
        const canonicalId = `uplisting_v3:${entry.uplistingMessageId}`
        if (canonical.some(message => message.message_id === canonicalId)) {
          next.delete(key)
          changed = true
        }
      }
      return changed ? next : current
    })
  }, [threadData?.messages, pendingSends])

  // Merges this thread's own pending sends (in the order they were started -
  // a plain Map preserves insertion order across the setPendingSends copies
  // above, since updating an existing key's value never moves it) after the
  // canonical list, so the newest one is always last - exactly where a real
  // reply would land. Memoized so its reference only changes when the
  // underlying data actually does - otherwise it's a fresh array on every
  // render (including ones unrelated to this thread's data), which would
  // make the auto-scroll effect below re-run far more often than the
  // exhaustive-deps lint rule is happy skipping.
  const messages = useMemo(() => {
    const canonical = threadData?.messages ?? []
    const pending: ThreadOrchestrationMessage[] = []
    for (const entry of pendingSends.values()) {
      if (entry.threadId !== threadId) continue
      pending.push({
        // Never collides with a real message_id (those are never prefixed
        // this way) - stable for this entry's whole lifetime, so React's
        // key-based reconciliation in thread-pane.tsx doesn't remount it on
        // every status transition.
        message_id: `pending:${entry.idempotencyKey}`,
        direction: 'host_to_guest',
        body: entry.text,
        channel: entry.channel,
        pendingStatus: entry.status,
        pendingError: entry.error,
        pendingIdempotencyKey: entry.idempotencyKey,
      })
    }
    return pending.length > 0 ? [...canonical, ...pending] : canonical
  }, [threadData?.messages, pendingSends, threadId])

  // Scrolls the thread pane to its latest message whenever the thread
  // changes or a new message arrives (Codex PR review): without this, a
  // conversation taller than the pane could open (or stay) scrolled away
  // from the newest guest message while the mark-read effect above has
  // already covered the whole fetched snapshot - an unseen message below
  // the fold could get silently marked read. Broader than firing once per
  // thread switch alone (an earlier version only fired the first time a
  // thread's messages loaded): also re-runs when a new message arrives in
  // an ALREADY-open thread, keyed on the last message's own id (not the
  // whole array, which gets a new reference whenever unrelated state
  // re-renders this component) so it only re-runs on a genuine content
  // change.
  useLayoutEffect(() => {
    const container = threadScrollRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, messages.length, messages[messages.length - 1]?.message_id])

  // Accepts the same guest-direction aliases as message-bubble.jsx /
  // thread-pane.jsx (Codex PR review: a payload using the legacy
  // 'inbound'/'guest' values, or omitting direction entirely, must still
  // be treated as replyable, not silently disable Send/Regenerate).
  //
  // Derived from the CANONICAL list only, never the pending-merged
  // `messages` above (Codex PR review on this ticket): every pending entry
  // - including a `failed`/`ambiguous` one - is appended as the thread's
  // last message, so reading liveMessage off `messages` would make
  // getLiveMessage() see a non-guest "last message" forever afterward, for
  // every canonical guest message that arrives from then on, not just the
  // one being replied to right now. A `failed` bubble would then silently
  // disable Send for the rest of the session - since it was never actually
  // delivered, the guest message it answers is exactly as unreplied as it
  // was before Send was clicked, and must stay replyable. The separate
  // hasActivePendingSendForLiveMessage guard below is what actually
  // prevents a genuine double-send while a `sending`/`sent` attempt is in
  // flight, instead of overloading this selector to do it.
  const liveMessage = getLiveMessage(threadData?.messages)
  const suggestion = liveMessage?.suggestions?.latest ?? null
  // True while THIS exact guest message already has a reply that has gone
  // out (or is going out) and Composer's own ordinary Send must not be
  // used to retry it. `failed` doesn't count - the message was definitely
  // never delivered, so the guest message it answers is exactly as
  // unreplied as it was before Send was clicked, and the composer's plain
  // Resend (same idempotencyKey, no duplicate risk) is the correct path.
  // `sending`/`sent` do count: the composer must not let the operator fire
  // a second, genuinely new reply at the same guest message while an
  // earlier one is still in flight or awaiting correlation (CLAUDE.md
  // "never sent twice"). `ambiguous` ALSO counts (Codex PR review): its
  // delivery outcome is genuinely unknown, and the only sanctioned way to
  // retry it is thread-pane.tsx's explicit, duplicate-risk-labeled "Resend
  // anyway" - not Composer's ordinary Send, which would silently reuse the
  // still-retained idempotency claim and, on the claim rejection that
  // follows, overwrite this ambiguous outcome with a `failed` one that
  // falsely asserts the message never went out.
  const hasActivePendingSendForLiveMessage = liveMessage
    ? Array.from(pendingSends.values()).some(
        entry =>
          entry.threadId === threadId &&
          entry.messageId === liveMessage.message_id &&
          (entry.status === 'sending' || entry.status === 'sent' || entry.status === 'ambiguous')
      )
    : false
  // Derived straight from the fetched data rather than local
  // "did this page start a generation" state (Codex PR review) - covers
  // BOTH a generation this page kicked off AND one that was already
  // queued/generating/streaming when the thread was opened or reloaded.
  // Purely a function of this thread's own query data, so switching
  // threads can never carry a stale generating flag from one to another.
  const isGenerating = isSuggestionInProgress(suggestion?.status)

  // Once the bounded poll above gives up without a terminal status, the
  // operator would otherwise be stuck looking at "Thinking…" forever with
  // no further refetch scheduled (Codex PR review) - reads the SAME
  // baseline the refetchInterval callback maintains (queryClient's own
  // cached query state, kept in sync with what that callback saw), so it's
  // never out of step with whether automatic polling actually stopped.
  const threadQueryState = queryClient.getQueryState(['thread-messages', threadId])

  const pollBaseline = pollBaselineRef.current.get(threadId)
  const isPollExhausted =
    isGenerating &&
    pollBaseline != null &&
    getFetchAttemptCount(threadQueryState) - pollBaseline >= MAX_SUGGESTION_POLL_ATTEMPTS

  // Same shape as isPollExhausted above, for a `sent` pendingSends entry
  // whose confirmation poll ran out without the canonical message ever
  // showing up: the bubble stays `sent` (Uplisting really did accept it -
  // this is not an error) and thread-pane.tsx offers the same "Check again"
  // affordance the suggestion flow does (handleCheckAgain below is generic
  // over what it's re-polling for).
  const hasSentPendingForThread = useMemo(
    () =>
      Array.from(pendingSends.values()).some(
        entry => entry.threadId === threadId && entry.status === 'sent'
      ),
    [pendingSends, threadId]
  )
  const isSendConfirmationExhausted =
    hasSentPendingForThread &&
    pollBaseline != null &&
    getFetchAttemptCount(threadQueryState) - pollBaseline >= MAX_SUGGESTION_POLL_ATTEMPTS

  // Composer only renders its own Regenerate control once a suggestion
  // object already exists (see composer.jsx's `status !== 'not-generated'`
  // guard) - a live guest message that has no suggestion yet (e.g. one the
  // backend's auto-generation hasn't reached) would otherwise leave the
  // operator with no way to produce one, and Send would always fail with
  // "Generate a suggestion before sending" (Codex PR review). This local
  // "Generate suggestion" control fills that gap without modifying the
  // shared Composer component. Only guards against a double-click while
  // the POST (and the refetch that follows it) is in flight - actual
  // generation progress is tracked by `isGenerating` above via
  // useThreadMessages' own polling.
  //
  // A Set of thread IDs with a submission in flight, not a single
  // replaceable value (Codex PR review, a further pass): a lone "which
  // thread" ID gets silently overwritten when a second thread starts its
  // own submission, and then cleared entirely by whichever of the two
  // finishes first - leaving the OTHER thread showing as no-longer-
  // submitting (Generate re-enabled) even though its own request/refetch
  // is still genuinely in flight. Every add/remove below is independent
  // per thread ID.
  const [submittingThreadIds, setSubmittingThreadIds] = useState(
    () => new Set<string | undefined>()
  )
  const isSubmittingGenerate = submittingThreadIds.has(threadId)

  const handleRegenerate = async (
    currentSuggestion: ThreadOrchestrationSuggestion | null
  ): Promise<void> => {
    if (!liveMessage || submittingThreadIds.has(threadId)) return
    const startedForThreadId = threadId
    setSubmittingThreadIds(current => new Set(current).add(startedForThreadId))
    // A fresh, operator-triggered generation always gets its own poll
    // budget, never inheriting an earlier exhausted count for this same
    // thread (mirrors suggested-review-block.jsx's regenerate success
    // handler) - cleared before the refetch below, so the refetchInterval
    // callback re-establishes the baseline from this fetch onward.
    pollBaselineRef.current.delete(startedForThreadId)
    // regenerateSuggestion's own promise resolves as soon as it has
    // synchronously invoked the 'done'/'error' callback below, not once
    // this refetch (captured from inside that callback) has itself
    // settled - awaited explicitly after, so the lock below isn't released
    // while the query still holds the OLD suggestion state (Codex PR
    // review: releasing it early let a second click enqueue a duplicate
    // generation/version before the UI had any evidence the first one had
    // actually been recorded).
    let refetchPromise: Promise<unknown> = Promise.resolve()
    try {
      await api.regenerateSuggestion(
        liveMessage.message_id,
        // The JSDoc on api.regenerateSuggestion (src/api/client.js) declares
        // `instruction`/`instructions`/`mode` without marking the first two
        // optional, so TS infers all three as required even though the
        // implementation defaults `options` to `{}` and never requires them
        // at runtime. Asserted against the function's own declared
        // parameter type rather than widening client.js here (out of scope
        // for this conversion, and owned by a concurrent conversion).
        { mode: currentSuggestion ? 'regenerate' : 'generate' } as Parameters<
          typeof api.regenerateSuggestion
        >[1],
        (eventType: string, data?: { message?: string }) => {
          if (eventType === 'done') {
            // Explicitly targets the thread this generation was started
            // for, NOT threadQuery.refetch() (Codex PR review: if the
            // operator navigates to a different thread before this 'done'
            // event fires, the mounted useThreadMessages hook's refetch
            // now tracks the NEW thread, so calling it here would refresh
            // the wrong query - leaving startedForThreadId's cache stale
            // and, since its submission lock is released once this
            // promise settles regardless, letting a return visit start a
            // duplicate generation against data that was never actually
            // confirmed current).
            refetchPromise = queryClient.refetchQueries({
              queryKey: ['thread-messages', startedForThreadId],
              exact: true,
              // Without this, refetchQueries only refetches ACTIVE
              // (currently-observed) queries by default - if the operator
              // has already navigated away, startedForThreadId's query is
              // inactive, and the refetch this whole fix exists for would
              // silently no-op instead of updating its cache.
              type: 'all',
            })
          } else if (eventType === 'error') {
            toast({
              title: 'Failed to regenerate suggestion',
              description: data?.message || 'Please try again',
              variant: 'destructive',
            })
          }
        }
      )
      await refetchPromise
    } catch (error) {
      toast({
        title: 'Failed to regenerate suggestion',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setSubmittingThreadIds(current => {
        if (!current.has(startedForThreadId)) return current
        const next = new Set(current)
        next.delete(startedForThreadId)
        return next
      })
    }
  }

  // Exposed as "Check again" once the poll budget above was exhausted
  // without a terminal status (Codex PR review) - resets this thread's
  // baseline (so the refetchInterval callback gives it a fresh window once
  // this refetch lands) and kicks one refetch to restart automatic polling
  // immediately, rather than waiting for some unrelated event to do it.
  const handleCheckAgain = (): void => {
    const startedForThreadId = threadId
    pollBaselineRef.current.delete(startedForThreadId)
    queryClient.refetchQueries({
      queryKey: ['thread-messages', startedForThreadId],
      exact: true,
      type: 'all',
    })
  }

  // Set of thread IDs with a dismiss-nudge request in flight - same
  // per-thread-independent tracking as submittingThreadIds above, for the
  // same reason (a second thread's own in-flight dismissal must never be
  // affected by this one finishing first).
  const [dismissingThreadIds, setDismissingThreadIds] = useState(
    () => new Set<string | undefined>()
  )
  const isDismissingNeedsReply = dismissingThreadIds.has(threadId)

  // "No reply needed" - marks the thread as not needing a reply without
  // sending anything (root CLAUDE.md: "Do not show UI controls that have no
  // backend behavior behind them" - this calls a real endpoint that records
  // the dismissal against the live guest message, see api.dismissThreadNeedsReply).
  //
  // Optimistic (CAP-355), the same race handleSend already solves for a
  // sent message: the nudge and the conversation-list dot both derive from
  // `needs_reply`, so waiting for the round trip left the operator with no
  // feedback at all. Switch to another thread and back before the refetch
  // lands and the thread still reads as needing a reply, which invites
  // dismissing it a second time because nothing on screen confirmed the
  // first.
  //
  // Done through the query cache rather than a local Set, because the two
  // things that must update read from two different caches in two
  // different components - the nudge from `thread-messages`, the dot from
  // the conversation list's own infinite query. The server's answer
  // replaces both on success, via the refetch/invalidate that were always
  // here; on failure the snapshots go back.
  const handleDismissNeedsReply = async (): Promise<void> => {
    if (!threadId || dismissingThreadIds.has(threadId)) return
    const startedForThreadId = threadId
    setDismissingThreadIds(current => new Set(current).add(startedForThreadId))

    const threadQueryKey = ['thread-messages', startedForThreadId] as const
    const previousThread = queryClient.getQueryData(threadQueryKey)
    const previousLists = queryClient.getQueriesData({ queryKey: listQueryKeyPrefix })

    queryClient.setQueryData(threadQueryKey, (current: unknown) =>
      withThreadNeedsReplyCleared(current)
    )
    queryClient.setQueriesData({ queryKey: listQueryKeyPrefix }, (current: unknown) =>
      withListNeedsReplyCleared(current, startedForThreadId)
    )

    try {
      await api.dismissThreadNeedsReply(startedForThreadId)
      await queryClient.refetchQueries({
        queryKey: ['thread-messages', startedForThreadId],
        exact: true,
        type: 'all',
      })
      queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix })
    } catch (error) {
      // Put back exactly what was there, so the nudge and the dot reappear
      // immediately rather than at the end of another round trip - the
      // backend never recorded the dismissal, and a UI still claiming it
      // did is worse than the delay this whole change removes.
      queryClient.setQueryData(threadQueryKey, previousThread)
      for (const [queryKey, data] of previousLists) {
        queryClient.setQueryData(queryKey, data)
      }
      // The snapshots are this client's last known state, not the
      // server's; let the server correct them in the background.
      queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix })
      toast({
        title: 'Failed to dismiss reply nudge',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setDismissingThreadIds(current => {
        if (!current.has(startedForThreadId)) return current
        const next = new Set(current)
        next.delete(startedForThreadId)
        return next
      })
    }
  }

  // Shared by handleSend (a brand-new logical send) and
  // handleResendPendingMessage (retrying a `failed`/`ambiguous` entry with
  // the identical idempotencyKey/messageId/version - admin-api's send
  // coordinator already treats a repeat of the same key as safe). Inserts
  // the pending entry BEFORE the network call so it renders with no network
  // wait (Definition of Done), and never lets a rejection escape without
  // first recording it into pendingSends - the caller's own catch/throw is
  // for its own purposes (composer.jsx's toast+preserved-draft on a
  // brand-new send; a swallowed rejection on resend, see below), not for
  // deciding what the bubble shows.
  async function performSend({
    startedForThreadId,
    messageId,
    version,
    text,
    channel,
    idempotencyKey,
    originalIdempotencyKey,
    hasUnresolvedAmbiguity = false,
  }: {
    startedForThreadId: string | undefined
    messageId: string
    version: number
    text: string
    channel?: string
    idempotencyKey: string
    originalIdempotencyKey: string
    /** Carried forward from a prior attempt in this same retry chain - see
     * PendingSendEntry.hasUnresolvedAmbiguity. Only handleResendPendingMessage
     * ever passes `true`; a brand-new send (handleSend) has no prior
     * attempt to inherit ambiguity from. */
    hasUnresolvedAmbiguity?: boolean
  }): Promise<void> {
    // A fresh logical send (or a resend of a stuck-exhausted one) always
    // gets its own poll budget, mirroring handleRegenerate - cleared before
    // the request, so the refetchInterval callback re-establishes the
    // baseline from this send's own status transition onward.
    pollBaselineRef.current.delete(startedForThreadId)
    setPendingSends(current => {
      const next = new Map(current)
      next.set(idempotencyKey, {
        idempotencyKey,
        originalIdempotencyKey,
        threadId: startedForThreadId,
        messageId,
        version,
        text,
        channel,
        status: 'sending',
        hasUnresolvedAmbiguity,
      })
      return next
    })
    try {
      const result = await api.sendSuggestionMessage(messageId, version, {
        body: text,
        idempotencyKey,
      })
      setPendingSends(current => {
        const existing = current.get(idempotencyKey)
        if (!existing) return current
        const next = new Map(current)
        next.set(idempotencyKey, {
          ...existing,
          status: 'sent',
          uplistingMessageId: result.uplistingMessageId ?? null,
          error: undefined,
        })
        return next
      })
      // Deliberately NOT awaited (Definition of Done: "the composer is
      // immediately ready for the next message" - this promise resolving
      // is what lets composer.tsx clear its draft and stop showing
      // "Sending…", and that must happen the instant the send itself
      // succeeds, not after an extra network round trip whose only job is
      // an early-arrival optimization). The refetchInterval-driven
      // confirmation poll above will catch the canonical message on its own
      // regardless of whether this particular refetch wins the race.
      queryClient.refetchQueries({
        queryKey: ['thread-messages', startedForThreadId],
        exact: true,
        type: 'all',
      })
      // The conversation list (and its "Needs reply" filter) is a separate
      // query cache from thread-messages - without this it kept showing the
      // stale preview, and an answered conversation kept appearing under
      // "Needs reply" until something unrelated happened to refetch it
      // (Codex PR review, from before this file tracked pending sends).
      queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix })
    } catch (error) {
      const errStatus = (error as { status?: number } | undefined)?.status
      const errCode = (error as { body?: { code?: string } } | undefined)?.body?.code
      // admin-api's uplisting-send-message route returns this exact
      // {code, status} pair only when Uplisting's own delivery outcome is
      // genuinely unknown (see uplisting-send-message.ts) - a rejection
      // with any OTHER status/body means admin-api itself received the
      // request and definitively classified it (validation, a claim
      // rejection, a definite Uplisting error), so `failed`/plain Resend
      // (no duplicate risk) is the correct default for that case.
      //
      // But this catch also runs for two error shapes admin-api never
      // produced at all (Codex PR review): ApiUnreachableError (the
      // response never arrived - the request may well have reached
      // admin-api and gone through before the connection dropped) and
      // ResponseValidationError (admin-api answered 2xx - a genuine
      // success - but the body didn't match the published contract shape).
      // Neither is a "the message never went out" fact, so asserting
      // `failed` for them would be actively wrong in the direction that
      // matters most: it would tell the operator it's safe to plain-Resend
      // (same idempotencyKey) a message that might already be delivered.
      // Both fold into `ambiguous` instead - the same "delivery unknown,
      // resending is a deliberate duplicate-risk action" treatment.
      const isAmbiguousThisAttempt =
        (errStatus === 502 && errCode === 'uplisting_send_ambiguous') ||
        error instanceof ApiUnreachableError ||
        error instanceof ResponseValidationError
      setPendingSends(current => {
        const existing = current.get(idempotencyKey)
        if (!existing) return current
        // Once ANY attempt in this retry chain has come back ambiguous,
        // THIS attempt's own definitive rejection must never downgrade the
        // bubble to a plain `failed` (Codex PR review) - it only proves
        // this particular retry didn't go through, not that an earlier,
        // genuinely ambiguous attempt didn't already deliver the message.
        const stillAmbiguous = hasUnresolvedAmbiguity || isAmbiguousThisAttempt
        const next = new Map(current)
        next.set(idempotencyKey, {
          ...existing,
          status: stillAmbiguous ? 'ambiguous' : 'failed',
          hasUnresolvedAmbiguity: stillAmbiguous,
          error: error instanceof Error ? error.message : 'Please try again',
        })
        return next
      })
      throw error
    }
  }

  const handleSend = async (
    text: string,
    { idempotencyKey, acceptedSuggestion }: ThreadOrchestrationSendMeta
  ): Promise<void> => {
    if (!liveMessage) {
      throw new Error('There is no pending guest message to reply to')
    }
    // Defense in depth alongside the disabled prop callers thread from
    // hasActivePendingSendForLiveMessage above - a genuinely new reply
    // must never go out through this ordinary path while an earlier one
    // for this exact guest message is still sending, awaiting
    // correlation, or ambiguous (Codex PR review - an ambiguous outcome
    // may only be retried via the explicit, duplicate-risk "Resend
    // anyway" action, never an ordinary Send).
    if (hasActivePendingSendForLiveMessage) {
      throw new Error('A reply to this message is already sending or awaiting confirmation')
    }
    const version = acceptedSuggestion?.version ?? liveMessage.suggestions?.latest?.version
    if (version == null) {
      throw new Error('Generate a suggestion before sending')
    }
    // Captured before the request goes out - liveMessage/version are read
    // from THIS thread's data at call time, unaffected by a later
    // navigation, but performSend's own continuations must stay pinned to
    // this same thread too (Codex PR review, same root cause as
    // handleRegenerate above).
    await performSend({
      startedForThreadId: threadId,
      messageId: liveMessage.message_id,
      version,
      text,
      channel: liveMessage.channel,
      idempotencyKey,
      originalIdempotencyKey: idempotencyKey,
    })
  }

  // Retries a `failed`/`ambiguous` pending bubble's Resend action -
  // thread-pane.tsx calls this directly (there is no Composer draft
  // involved), so unlike handleSend a rejection here has no caller left to
  // usefully catch it: performSend already recorded the new outcome into
  // pendingSends (that IS the UI update this action produces), so the
  // rejection is swallowed rather than surfacing as an unhandled promise
  // rejection.
  // Guards a rapid double-click on Resend/Resend-anyway before React has
  // had a chance to re-render with this entry's status flipped to
  // `sending` (low-effort code review finding): without this, two
  // invocations in the same tick would both read the SAME stale `entry`
  // from the pendingSends closure, each generate its own idempotencyKey
  // (the ambiguous branch below always mints a fresh one), and both call
  // performSend - firing two real, concurrent sends of the same reply.
  // Keyed by the entry's own idempotencyKey (what the button's onClick
  // actually passes), not the resend's fresh key, since that's what two
  // clicks on the SAME rendered button would share. Mirrors
  // submittingThreadIds/dismissingThreadIds above for the identical
  // reason - a ref, not state, because it must be checked synchronously
  // before any state update or await has a chance to land.
  const resendInFlightRef = useRef(new Set<string>())

  const handleResendPendingMessage = (idempotencyKey: string): void => {
    const entry = pendingSends.get(idempotencyKey)
    if (!entry || entry.status === 'sending') return
    if (resendInFlightRef.current.has(idempotencyKey)) return
    resendInFlightRef.current.add(idempotencyKey)
    // An `ambiguous` outcome means admin-api's send coordinator DELIBERATELY
    // kept the idempotency claim rather than releasing it (Uplisting's own
    // delivery outcome is unknown - releasing it could let an ordinary
    // retry resend a message that already went out). Reusing that same key
    // here would not perform the "Resend anyway" the operator asked for at
    // all - it would just hit the retained claim and come back as a plain
    // rejection, which performSend would otherwise misclassify as `failed`
    // (Codex PR review), silently telling the operator delivery is now
    // KNOWN to have failed when it is still genuinely unknown. A real
    // resend of an ambiguous send is - and must be - a brand-new attempt
    // with its own key: that new attempt IS the duplicate-message risk the
    // button's own copy already warns about, not a false one. `failed`
    // entries have no such retained claim (admin-api explicitly released
    // it - see uplisting-send-message.ts), so those keep reusing the exact
    // same key, an ordinary safe retry.
    const isAmbiguousResend = entry.status === 'ambiguous'
    const resendIdempotencyKey = isAmbiguousResend ? crypto.randomUUID() : entry.idempotencyKey
    if (isAmbiguousResend) {
      setPendingSends(current => {
        if (!current.has(entry.idempotencyKey)) return current
        const next = new Map(current)
        next.delete(entry.idempotencyKey)
        return next
      })
    }
    performSend({
      startedForThreadId: entry.threadId,
      messageId: entry.messageId,
      version: entry.version,
      text: entry.text,
      channel: entry.channel,
      idempotencyKey: resendIdempotencyKey,
      // Stable across the whole chain regardless of how many resends (and
      // fresh ambiguous-resend keys) came before this one - Composer only
      // ever held the FIRST attempt's key.
      originalIdempotencyKey: entry.originalIdempotencyKey,
      // Carries this entry's own ambiguity forward into the new attempt
      // (Codex PR review): a `failed` entry never had unresolved ambiguity
      // to carry (see PendingSendEntry's own invariant), so this is only
      // ever true when resending an `ambiguous` entry.
      hasUnresolvedAmbiguity: entry.status === 'ambiguous',
    })
      .then(() => {
        // Composer.tsx deliberately preserves its draft text AND its
        // pendingIdempotencyKeyRef (matching this chain's ORIGINAL
        // idempotencyKey - never one of the fresh network keys a later
        // ambiguous resend generated, which Composer never saw) after the
        // original send failed/came back ambiguous, so the operator can
        // edit and retry from the composer itself. This inline Resend
        // bypasses Composer entirely, so on success it has no way to know
        // the text it's still showing was just delivered (Codex PR
        // review) - left alone, the stale draft could later be
        // re-submitted reusing an idempotencyKey admin-api already
        // consumed for a different message, which it correctly rejects,
        // but with a confusing failure toast over text that was never
        // actually unsent. Composer's own effect (keyed on this value)
        // clears its draft only when it still matches.
        setLastInlineResendSentKey(entry.originalIdempotencyKey)
      })
      .catch(() => {})
      .finally(() => {
        resendInFlightRef.current.delete(idempotencyKey)
      })
  }

  return {
    threadQuery,
    threadData,
    propertyMismatch,
    messages,
    setThreadScrollNode,
    liveMessage,
    suggestion,
    isGenerating,
    isPollExhausted,
    isSendConfirmationExhausted,
    hasActivePendingSendForLiveMessage,
    isSubmittingGenerate,
    isDismissingNeedsReply,
    handleSend,
    handleResendPendingMessage,
    lastInlineResendSentKey,
    handleRegenerate,
    handleCheckAgain,
    handleDismissNeedsReply,
  }
}
